import { logger } from '@tunasong/models'
import { useRef, useState } from 'react'

export type SaveStatus = 'idle' | 'saving' | 'error'

/**
 * Tracks profile writes for the setup editor's status line: "Saving…" while the latest write is in flight, an error
 * with Retry when it fails, idle once it is confirmed. Only the latest write decides the status.
 */
export const useSaveTracker = () => {
  const [status, setStatus] = useState<SaveStatus>('idle')
  const latest = useRef(0)
  const lastAction = useRef<(() => Promise<unknown>) | null>(null)

  const track = (action: () => Promise<unknown>) => {
    latest.current += 1
    const seq = latest.current
    lastAction.current = action
    setStatus('saving')
    return action().then(
      () => {
        if (seq === latest.current) {
          setStatus('idle')
        }
      },
      (error: unknown) => {
        logger.warn('Saving the stage setup failed', { error })
        if (seq === latest.current) {
          setStatus('error')
        }
      }
    )
  }

  const retry = async () => {
    const action = lastAction.current
    if (action) {
      await track(action)
    }
  }

  return { status, track, retry }
}

export type SaveTracker = ReturnType<typeof useSaveTracker>
